import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthSession, useTranslation } from '@dukanti/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { supabase } from '@/lib/supabase';
import { useCurrentStore } from '@/hooks/use-current-store';
import {
  newsletterSubscriberPermissionKeys,
  useNewsletterSubscriberPermissions,
} from '@/hooks/use-newsletter-subscriber-permissions';
import { NewsletterSubscribersPage } from '@/features/newsletter-subscribers/newsletter-subscribers-page';

export function SubscribersRoute(): React.JSX.Element {
  const { t, dir } = useTranslation();
  const session = useAuthSession(supabase);
  const store = useCurrentStore(session);
  const permissions = useNewsletterSubscriberPermissions(store.storeId);
  const queryClient = useQueryClient();
  const scope = `${store.storeId ?? ''}:${session.user?.id ?? ''}`;
  const [deniedScope, setDeniedScope] = useState<string | null>(null);
  const onForbidden = useCallback((): void => {
    setDeniedScope(scope);
    queryClient.setQueryData(
      newsletterSubscriberPermissionKeys.role(store.storeId, session.user?.id ?? null),
      {
        role: null,
        canManageNewsletterSubscribers: false,
      },
    );
  }, [scope, store.storeId, session.user?.id, queryClient]);
  const loading = session.isLoading || store.isLoading || permissions.isLoading;
  const allowed =
    permissions.data?.canManageNewsletterSubscribers === true && deniedScope !== scope;
  return (
    <main
      dir={dir}
      data-testid="subscribers-route"
      className="mx-auto w-full max-w-7xl px-4 py-6 text-start sm:px-6 lg:px-8"
    >
      <h1 className="font-display text-foreground text-3xl font-bold">
        {t('admin.newsletterSubscribers.title')}
      </h1>
      <p className="text-muted-foreground mb-6 mt-2">
        {t('admin.newsletterSubscribers.description')}
      </p>
      {loading ? (
        <div aria-busy="true" aria-label={t('common.loading')} className="space-y-3">
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-20 w-full" />
        </div>
      ) : store.isError || permissions.isError ? (
        <div role="alert" className="space-y-4">
          <p>{t('admin.newsletterSubscribers.loadError')}</p>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              store.refetch();
              void permissions.refetch();
            }}
          >
            {t('common.retry')}
          </Button>
        </div>
      ) : !allowed || !store.storeId ? (
        <p role="alert" className="border-border bg-card shadow-admin-card rounded-xl border p-6">
          {t('admin.newsletterSubscribers.forbidden')}
        </p>
      ) : (
        <NewsletterSubscribersPage
          key={scope}
          client={supabase}
          storeId={store.storeId}
          onForbidden={onForbidden}
        />
      )}
    </main>
  );
}
